import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, filter, firstValueFrom, map, take } from 'rxjs';
import { Auth, User, UserCredential, inMemoryPersistence, getAuth, onAuthStateChanged, setPersistence, signInWithEmailAndPassword, signOut } from 'firebase/auth';

import { FirebaseAppService } from './firebase-app.service';

@Injectable({
  providedIn: 'root'
})
export class FirebaseAuthClientService {
  private readonly auth: Auth;
  private readonly userSubject = new BehaviorSubject<User | null | undefined>(undefined);

  /** `undefined` means "not initialized yet". */
  readonly user$: Observable<User | null | undefined> = this.userSubject.asObservable();
  readonly isLoggedIn$: Observable<boolean | undefined> = this.user$.pipe(
    map((u) => (typeof u === 'undefined' ? undefined : !!u))
  );

  constructor(firebaseAppService: FirebaseAppService) {
    this.auth = getAuth(firebaseAppService.getApp());

    // Session cleared on page reload — user must log in again.
    setPersistence(this.auth, inMemoryPersistence).catch(() => {
      // ignore
    });

    onAuthStateChanged(this.auth, (user) => this.userSubject.next(user));
  }

  async signIn(email: string, password: string): Promise<UserCredential> {
    await setPersistence(this.auth, inMemoryPersistence);
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  async signOut(): Promise<void> {
    await signOut(this.auth);
  }

  getCurrentUser(): User | null {
    return this.auth.currentUser;
  }

  async getCurrentIdTokenWhenReady(): Promise<string | null> {
    const state = this.userSubject.value;

    if (typeof state === 'undefined') {
      const resolvedUser = await firstValueFrom(this.waitForUserOnce());
      return resolvedUser ? resolvedUser.getIdToken() : null;
    }

    return state ? state.getIdToken() : null;
  }

  /** Waits until auth state is known (not `undefined`). */
  waitForUserOnce(): Observable<User | null> {
    return this.user$.pipe(
      filter((u): u is User | null => typeof u !== 'undefined'),
      take(1)
    );
  }
}
